import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import Message from "../../log/Message";
import {MessageType} from "../../log/MessageInterface";
import AbstractDialog, {AbstractDialogProperties, AbstractDialogState} from "./AbstractDialog";
import Translate from "../../translation/Translate";
import Icon from "../../components/Icon";
import DateHelper from "../../helper/DateHelper";

interface RefreshDialogState extends AbstractDialogState {
	timeout: string
}

export class RefreshDialogDefinition extends AbstractDialog<AbstractDialogProperties, RefreshDialogState> {
	private refreshInterval: number;

	constructor(props: Readonly<AbstractDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			timeout: ""
		};

		this.refreshInterval = window.setInterval(() => {
			if (!this.mounted || !this.props.visible) {
				return;
			}

			this.setState({
				timeout: DateHelper.formatTime(
					this.props.context.session.getSessionExpiry()
				)
			})
		}, 1000);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["refresh-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<fieldset className="p-col-12 p-grid">
						<div className="dialog-message">
							<Translate {...this.props} data="refresh.message" namespace="dialog"
									   values={{
										   timeout: this.state.timeout
									   }}/>
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-user"}/>
					<Translate {...this.props} data="refresh.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div>
				<Button label={Translate.value("buttons.refresh")} disabled={!this.isValid()}
						icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-rotate-270 small")}
						className="primary-button" onClick={() => this.submit()}/>
				<Button label={Translate.value("buttons.close")} icon="icon icon-close" onClick={() => this.hide()}
						className="dialog-button"/>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted;
	}

	protected async submit(): Promise<void> {
		this.setState({
			exception: undefined,
			submitted: true
		});

		try {
			await this.props.context.session.refresh();
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.authentication.refresh", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.hide();
	}
}

const RefreshDialog = React.memo(RefreshDialogDefinition,
	(prevProps: Readonly<AbstractDialogProperties>, nextProps: Readonly<AbstractDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default RefreshDialog;